import React, { useRef } from 'react';
import { FuelLogEntry } from '../lib/fuelLogApi';
import { formatEntryDate } from '../lib/dateUtils';
import SwipeToDelete from 'react-swipe-to-delete-ios';
import { formatNumberSR, formatCurrencySR } from '../lib/formatUtils';

interface FuelLogEntryRowProps {
  entry: FuelLogEntry;
  onEdit: (entry: FuelLogEntry) => void;
  onDelete: (id: string) => void;
}

const SWIPE_THRESHOLD = 30;

const FuelLogEntryRow: React.FC<FuelLogEntryRowProps> = ({ entry, onEdit, onDelete }) => {
  const startXRef = useRef<number | null>(null);
  const swipedRef = useRef(false);

  const handleDelete = (id: string) => {
    onDelete(id);
  };

  const handleClick = () => {
    if (swipedRef.current) {
      swipedRef.current = false;
      return;
    }
    onEdit(entry);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    swipedRef.current = false;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (startXRef.current !== null) {
      const endX = e.changedTouches[0].clientX;
      if (Math.abs(endX - startXRef.current) > SWIPE_THRESHOLD) {
        swipedRef.current = true;
      }
      startXRef.current = null;
    }
    setTimeout(() => { swipedRef.current = false; }, 100);
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    startXRef.current = e.clientX;
    swipedRef.current = false;
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    if (startXRef.current !== null) {
      const endX = e.clientX;
      if (Math.abs(endX - startXRef.current) > SWIPE_THRESHOLD) {
        swipedRef.current = true;
      }
      startXRef.current = null;
    }
    setTimeout(() => { swipedRef.current = false; }, 100);
  };

  return (
    <div className="mb-1">
      <SwipeToDelete
        onDelete={() => handleDelete(entry.id)}
        deleteColor="transparent"
        deleteComponent={
          <div className="flex h-full items-center justify-end pr-2">
            <button
              className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-1 rounded shadow transition-colors min-h-0 min-w-[70px]"
              style={{ fontSize: 14 }}
              onClick={e => { e.stopPropagation(); handleDelete(entry.id); }}
            >
              Delete
            </button>
          </div>
        }
        height={48}
        transitionDuration={250}
        rtl={false}
      >
        <div
          className="bg-theme-primary rounded-lg shadow-sm border border-theme-primary flex items-center justify-center min-h-[48px] cursor-pointer"
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <div className="flex w-full items-center text-xs font-semibold min-h-[32px] px-2">
            <span className="text-white flex-1 text-left">{formatNumberSR(entry.liter, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L</span>
            <span className="text-theme-secondary flex-1 text-center">{formatEntryDate(entry.date)}</span>
            <span className="text-primary-600 flex-1 text-right">{formatCurrencySR(entry.price)}</span>
          </div>
        </div>
      </SwipeToDelete>
    </div>
  );
};

export default FuelLogEntryRow; 